<script setup>
// A/B testleri (Meta tarafinda "ad study").
//
// Test suren bir calismanin ortasinda ayar degistirmek sonucu bozdugu icin bu
// sekme yalnizca listeler ve detayini acar; yeni test kurmak Meta arayuzunden
// yapilan bilincli bir istir.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const route = useRoute();

const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');
const islemdeki = ref('');
const detay = ref(null);

// Meta bu alanlari bazen unix saniye, bazen ISO metin dondurur; cozumlemeyi
// tek yerde tutuyoruz ki tablo ile ayrinti paneli ayni tarihi gostersin.
const tariheCevir = deger => {
  const sayi = Number(deger);
  const tarih =
    Number.isFinite(sayi) && String(deger).length <= 11
      ? new Date(sayi * 1000)
      : new Date(deger);
  return Number.isNaN(tarih.getTime()) ? null : tarih;
};

const tarihBicimle = deger => {
  if (!deger) return '—';
  const tarih = tariheCevir(deger);
  return tarih ? tarih.toLocaleDateString('tr-TR') : String(deger);
};

// Ayrintida saat de gerekiyor: bir testin gun icinde ne zaman bittigi
// yalnizca gun gosterilince anlasilmiyor.
const tarihSaatBicimle = deger => {
  if (!deger) return '—';
  const tarih = tariheCevir(deger);
  if (!tarih) return String(deger);
  return tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const sutunlar = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.ORTAK.AD') },
  { anahtar: 'type', baslik: t('META_ADS.ORTAK.TUR') },
  { anahtar: 'description', baslik: t('META_ADS.ORTAK.ACIKLAMA') },
  { anahtar: 'start_time', baslik: t('META_ADS.ORTAK.BASLANGIC'), bicim: tarihBicimle },
  { anahtar: 'end_time', baslik: t('META_ADS.ORTAK.BITIS'), bicim: tarihBicimle },
]);

// Test ayrintisinda gruplar (cells) ve olculen hedefler ic ice dizi olarak
// gelir. JSON.stringify ciktisini panele basmak testin neyi neyle karsilastirdigini
// gizliyordu; onun yerine ad -> Turkce etiket, tarih -> bicimli deger, ic ice
// nesne -> "Ust > Alt" satirlari seklinde aciyoruz.
const ALAN_ETIKETLERI = {
  id: 'META_ADS.ALAN.KIMLIK',
  account_id: 'META_ADS.ALAN.HESAP',
  name: 'META_ADS.ORTAK.AD',
  type: 'META_ADS.ORTAK.TUR',
  description: 'META_ADS.ORTAK.ACIKLAMA',
  cells: 'META_ADS.ALAN.TEST_GRUPLARI',
  objectives: 'META_ADS.ALAN.OLCULEN_HEDEFLER',
  confidence_level: 'META_ADS.ALAN.GUVEN_DUZEYI',
  created_by: 'META_ADS.ALAN.OLUSTURAN',
  created_time: 'META_ADS.ORTAK.OLUSTURMA',
  updated_time: 'META_ADS.ALAN.GUNCELLEME',
  start_time: 'META_ADS.ORTAK.BASLANGIC',
  end_time: 'META_ADS.ORTAK.BITIS',
  observation_end_time: 'META_ADS.ALAN.GOZLEM_BITISI',
  canceled_time: 'META_ADS.ALAN.IPTAL_ZAMANI',
  campaign_id: 'META_ADS.ALAN.KAMPANYA',
  adset_id: 'META_ADS.REKLAMLAR.REKLAM_SETI',
  ad_id: 'META_ADS.ALAN.REKLAM',
  treatment_percentage: 'META_ADS.ALAN.TRAFIK_PAYI',
};

// Bicimi alan adindan seciyoruz. Testlerde tutar alani yok, bu yuzden para
// bicimi de yok; olmayan bir alani bicimlemek yanlis rakam uretir.
const TARIH_ALANLARI = [
  'created_time',
  'updated_time',
  'start_time',
  'end_time',
  'observation_end_time',
  'canceled_time',
];

// Iki seviyeden derini dar panelde okunmaz hale geliyor; daha derini ve uzun
// dizileri sayiyla ozetliyoruz.
const EN_FAZLA_DERINLIK = 2;
const EN_FAZLA_ACILAN_OGE = 3;

// Bilinmeyen alan adi oldugu gibi kalir; uydurma etiket yanlis bilgi verir.
const etiketle = anahtar => {
  const ceviri = ALAN_ETIKETLERI[anahtar];
  return ceviri ? t(ceviri) : anahtar;
};

const duzDeger = (anahtar, deger) => {
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.ORTAK.EVET') : t('META_ADS.ORTAK.HAYIR');
  }
  if (TARIH_ALANLARI.includes(anahtar)) return tarihSaatBicimle(deger);
  return String(deger);
};

const alanlaraCevir = (nesne, atlanacak = []) => {
  const cikti = [];

  const gez = (kaynak, ustAnahtar, ustEtiket, derinlik) => {
    Object.entries(kaynak || {}).forEach(([anahtar, deger]) => {
      if (atlanacak.includes(anahtar)) return;
      // Bos alan hic gosterilmiyor: "—" satirlari dolu alanlari gozden kacirir.
      if (deger === null || deger === undefined || deger === '') return;

      const tamAnahtar = ustAnahtar ? `${ustAnahtar}.${anahtar}` : anahtar;
      const etiket = ustEtiket ? `${ustEtiket} › ${etiketle(anahtar)}` : etiketle(anahtar);

      if (Array.isArray(deger)) {
        // Bos ogeleri once atiyoruz: aksi halde listede "null" yazisi cikiyor.
        const ogeler = deger.filter(
          oge => oge !== null && oge !== undefined && oge !== ''
        );
        if (!ogeler.length) return;
        if (ogeler.every(oge => typeof oge !== 'object')) {
          cikti.push({
            anahtar: tamAnahtar,
            etiket,
            deger: ogeler.map(oge => duzDeger(anahtar, oge)).join(', '),
          });
          return;
        }
        if (derinlik < EN_FAZLA_DERINLIK && ogeler.length <= EN_FAZLA_ACILAN_OGE) {
          ogeler.forEach((oge, sira) =>
            gez(oge, `${tamAnahtar}.${sira}`, `${etiket} ${sira + 1}`, derinlik + 1)
          );
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.OGE_SAYISI', { sayi: ogeler.length }),
        });
        return;
      }

      if (typeof deger === 'object') {
        const altSayi = Object.keys(deger).length;
        if (!altSayi) return;
        if (derinlik < EN_FAZLA_DERINLIK) {
          gez(deger, tamAnahtar, etiket, derinlik + 1);
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.ALAN_SAYISI', { sayi: altSayi }),
        });
        return;
      }

      cikti.push({ anahtar: tamAnahtar, etiket, deger: duzDeger(anahtar, deger) });
    });
  };

  gez(nesne, '', '', 0);
  return cikti;
};

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_ad_studies', {
      account_id: props.hesap,
    });
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

const detayAc = async satir => {
  islemdeki.value = satir.id;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_study_details', {
      study_id: satir.id,
    });
    const veri = Array.isArray(sonuc?.veri) ? sonuc.veri[0] : sonuc?.veri;
    detay.value = {
      ad: satir.name || satir.id,
      alanlar: alanlaraCevir(veri),
      ozet: sonuc?.ozet || '',
    };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <div class="flex items-center gap-3 mb-4">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('META_ADS.REKLAMLAR.AB_TESTLERI') }}
      </h2>
      <span class="grow" />
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('META_ADS.ORTAK.YENILE')"
        @click="yukle"
      />
    </div>

    <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>
    <p v-if="!hesap" class="text-sm text-n-slate-11">{{ t('META_ADS.ORTAK.HESAP_SECIN') }}</p>

    <div v-else class="flex items-start gap-6">
      <div class="flex-1 min-w-0">
        <AracTablosu
          :satirlar="satirlar"
          :sutunlar="sutunlar"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.REKLAMLAR.TEST_YOK')"
        >
          <template #eylemler="{ satir }">
            <Button
              variant="faded"
              size="sm"
              :label="t('META_ADS.ORTAK.DETAY')"
              :disabled="islemdeki === satir.id"
              @click="detayAc(satir)"
            />
          </template>
        </AracTablosu>
      </div>

      <aside
        v-if="detay"
        class="w-80 shrink-0 p-4 border rounded-lg border-n-weak bg-n-surface-1"
      >
        <div class="flex items-start gap-2 mb-3">
          <h3 class="text-sm font-medium text-n-slate-12 break-words">{{ detay.ad }}</h3>
          <span class="grow" />
          <Button variant="faded" size="sm" icon="i-lucide-x" @click="detay = null" />
        </div>
        <p v-if="detay.ozet" class="mb-3 text-xs text-n-slate-11">{{ detay.ozet }}</p>
        <dl
          v-if="detay.alanlar.length"
          class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs"
        >
          <template v-for="alan in detay.alanlar" :key="alan.anahtar">
            <dt class="break-words text-n-slate-11">{{ alan.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
          </template>
        </dl>
        <p v-else class="text-xs text-n-slate-11">
          {{ t('META_ADS.ORTAK.DETAY_YOK') }}
        </p>
      </aside>
    </div>
  </div>
</template>
